import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, MapPin, Sparkles, Quote, BookOpen, MessageCircle } from "lucide-react";
import ParticleField from "@/components/site/ParticleField";
import HeroVisual from "@/components/site/HeroVisual";
import SectionHeader from "@/components/site/SectionHeader";
import PlaybookDialog from "@/components/site/PlaybookDialog";
import LeadDialog from "@/components/site/LeadDialog";
import { whatsappUrl } from "@/lib/channels";
import {
  STATS, SERVICES, SOLUTIONS, INDUSTRIES, PROCESS, CASE_STUDIES,
  TECH_STACK, WHY_ZOLVIX, TESTIMONIALS, LOCATIONS,
} from "@/data/site";

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-hero-radial" />
        <ParticleField />
        <div className="relative container-zx grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="chip mb-5"
            >
              <Sparkles className="h-3.5 w-3.5 text-cyan" />
              <span>Software · SaaS · AI · Automation</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tighter text-white leading-[1.02]"
            >
              Engineering <span className="heading-grad">Digital Products</span> That Scale
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-base sm:text-lg text-ink-200 max-w-xl"
            >
              We help businesses solve complex challenges through software, SaaS platforms,
              AI-powered automation and enterprise digital transformation.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <LeadDialog
                source="hero_primary"
                titleVariant="project"
                trigger={
                  <button type="button" className="btn-primary" data-testid="hero-primary-cta">
                    Start Your Project <ArrowRight className="h-4 w-4" />
                  </button>
                }
              />
              <Link to="/solutions" className="btn-secondary" data-testid="hero-secondary-cta">
                Explore Solutions <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-ink-400">
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-cyan" /> Mumbai, India</div>
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-cyan" /> Dubai, UAE</div>
              <div className="hidden sm:block h-3 w-px bg-white/10" />
              <div>Trusted across 10+ industries</div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="relative">
        <div className="container-zx">
          <div className="rounded-2xl border border-white/[0.06] bg-[#0B2138]/80 backdrop-blur-xl shadow-card overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/[0.06]">
              {STATS.map((s, i) => (
                <div key={s.label} className="p-6 md:p-7" data-testid={`stat-${i}`}>
                  <div className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">
                    <CountUp end={s.value} duration={2} suffix={s.suffix} enableScrollSpy scrollSpyOnce />
                  </div>
                  <div className="mt-1.5 text-xs sm:text-sm text-ink-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32" id="services">
        <div className="container-zx">
          <SectionHeader
            overline="Services"
            title="Engineering capabilities, end to end"
            subtitle="From discovery and design to scale-grade engineering — Zolvix operates as your technology partner across the entire product lifecycle."
            testid="services-section-header"
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {SERVICES.map((s) => {
              const isAI = s.title === "AI & Automation Solutions";
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45 }}
                  className={"card-surface p-6 relative " + (isAI ? "border-cyan/30 shadow-[0_18px_60px_rgba(22,213,255,0.15)]" : "")}
                  data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {isAI && (
                    <span className="absolute top-4 right-4 chip text-[10px] tracking-[0.18em] uppercase border-cyan/40 text-cyan bg-cyan/5">
                      <BookOpen className="h-3 w-3" /> Free Playbook
                    </span>
                  )}
                  <div className="h-10 w-10 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-ink-400 leading-relaxed">{s.blurb}</p>
                  {isAI ? (
                    <PlaybookDialog
                      source="home_ai_card"
                      trigger={
                        <button
                          type="button"
                          className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan hover:text-white"
                          data-testid="home-playbook-trigger"
                        >
                          Get the Playbook <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      }
                    />
                  ) : (
                    <Link to="/services" className="mt-5 inline-flex items-center gap-1.5 text-xs text-cyan hover:text-white">
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="py-24 md:py-32 bg-[#050F1C]/60 border-y border-white/[0.04]" id="solutions">
        <div className="container-zx">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeader
              overline="Solutions"
              title="Platforms we've built, ready to be tailored to yours"
              subtitle="Battle-tested blueprints for the systems your business runs on — accelerating time to value without sacrificing fit."
              testid="solutions-section-header"
            />
            <Link to="/solutions" className="btn-secondary self-start" data-testid="solutions-explore-link">
              See all solutions <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.map((s) => (
              <div key={s.title} className="card-surface p-6 group" data-testid={`solution-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-ink-400 group-hover:text-cyan transition" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-400 leading-relaxed">{s.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32" id="industries">
        <div className="container-zx">
          <SectionHeader
            overline="Industries"
            title="Domain depth across regulated, high-stakes industries"
            subtitle="We bring patterns, vocabulary and reference architectures from the industries we know best."
            testid="industries-section-header"
          />
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((i) => (
              <div key={i.label} className="card-surface p-5 flex items-center gap-3" data-testid={`industry-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="h-9 w-9 rounded-md grid place-items-center bg-white/[0.04] text-cyan border border-white/10">
                  <i.icon className="h-4 w-4" />
                </div>
                <div className="text-sm text-white font-medium">{i.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="py-24 md:py-32 bg-[#050F1C]/60 border-y border-white/[0.04]" id="process">
        <div className="container-zx">
          <SectionHeader
            overline="How we work"
            title="A delivery process designed around outcomes, not artefacts"
            testid="process-section-header"
          />
          <div className="mt-12 relative">
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan/30 to-transparent" />
            <div className="space-y-10">
              {PROCESS.map((p, i) => (
                <motion.div
                  key={p.step}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={`relative pl-12 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-12 ${i % 2 === 0 ? "" : "sm:[&>*:first-child]:order-2"}`}
                  data-testid={`process-step-${p.step}`}
                >
                  <div className={`${i % 2 === 0 ? "sm:text-right sm:pr-12" : "sm:pl-12"}`}>
                    <div className="overline">Step {p.step}</div>
                    <h3 className="mt-1 font-display text-2xl font-semibold text-white">{p.title}</h3>
                    <p className="mt-2 text-sm text-ink-400 max-w-md sm:max-w-none">{p.body}</p>
                  </div>
                  <div className="hidden sm:block" />
                  <div className="absolute left-4 sm:left-1/2 top-1.5 -translate-x-1/2 h-3 w-3 rounded-full bg-cyan shadow-[0_0_0_6px_rgba(22,213,255,0.12)]" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES TEASER */}
      <section className="py-24 md:py-32" id="case-studies">
        <div className="container-zx">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeader
              overline="Case Studies"
              title="Real systems. Measurable outcomes."
              subtitle="A glimpse into the platforms we've shipped for ambitious operators."
              testid="case-studies-section-header"
            />
            <Link to="/case-studies" className="btn-secondary self-start" data-testid="case-studies-all-link">
              View all case studies <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {CASE_STUDIES.slice(0, 4).map((c) => (
              <article key={c.title} className="card-surface overflow-hidden" data-testid={`case-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-[#050F1C] to-[#0F2B48]">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className={
                      "absolute inset-0 h-full w-full opacity-95 " +
                      (c.fit === "contain" ? "object-contain p-4" : "object-cover")
                    }
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#071A2D] via-[#071A2D]/30 to-transparent" />
                  <div className="absolute top-3 left-3 chip bg-[#071A2D]/80 backdrop-blur">{c.industry}</div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-ink-400">{c.summary}</p>
                  <div className="mt-5 grid grid-cols-3 gap-3">
                    {c.metrics.map(([v, l]) => (
                      <div key={l} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                        <div className="text-base font-bold text-white">{v}</div>
                        <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TECH STACK MARQUEE */}
      <section className="py-20 bg-[#050F1C]/60 border-y border-white/[0.04]" id="tech">
        <div className="container-zx">
          <SectionHeader
            overline="Technology Stack"
            title="A modern, vendor-neutral engineering stack"
            testid="tech-section-header"
          />
        </div>
        <div className="mt-10 space-y-4">
          {Object.entries(TECH_STACK).map(([group, items], idx) => (
            <Marquee
              key={group}
              gradient
              gradientColor="#050F1C"
              speed={30 + idx * 6}
              direction={idx % 2 === 0 ? "left" : "right"}
              data-testid={`tech-marquee-${group.toLowerCase()}`}
            >
              {items.concat(items).map((t, i) => (
                <div key={t + i} className="mx-3 my-1 rounded-xl border border-white/[0.06] bg-[#0B2138] px-5 py-3 flex items-center gap-3">
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-cyan">{group}</span>
                  <span className="text-sm text-white font-medium">{t}</span>
                </div>
              ))}
            </Marquee>
          ))}
        </div>
      </section>

      {/* WHY ZOLVIX */}
      <section className="py-24 md:py-32">
        <div className="container-zx">
          <SectionHeader
            overline="Why Zolvix"
            title="Built for serious operators who care about outcomes"
            testid="why-section-header"
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_ZOLVIX.map((w) => (
              <div key={w.title} className="card-surface p-6" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="h-10 w-10 rounded-lg grid place-items-center bg-cyan/10 border border-cyan/25 text-cyan">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-ink-400 leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE */}
      <section className="py-24 md:py-32 bg-[#050F1C]/60 border-y border-white/[0.04]" id="presence">
        <div className="container-zx">
          <SectionHeader
            overline="Global Presence"
            title="Building Technology Beyond Borders"
            subtitle="Supporting clients globally through strategic delivery and business development hubs."
            testid="presence-section-header"
          />

          <div className="mt-12 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 relative rounded-2xl border border-white/[0.06] bg-[#0B2138]/70 overflow-hidden aspect-[16/10]">
              {/* Simple stylised world map (SVG dots) */}
              <WorldMap />
            </div>
            <div className="lg:col-span-5 space-y-4">
              {LOCATIONS.map((l) => (
                <div key={l.city} className="card-surface p-6 flex gap-4" data-testid={`location-${l.city.toLowerCase()}`}>
                  <img src={l.image} alt={l.city} className="h-20 w-20 rounded-xl object-cover" />
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-cyan" />
                      <div className="font-display text-lg font-semibold text-white">{l.city}, {l.country}</div>
                    </div>
                    <p className="mt-1 text-sm text-ink-400">{l.role}</p>
                    <Link to="/contact" className="mt-2 inline-flex items-center gap-1.5 text-xs text-cyan hover:text-white">
                      Reach this office <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="container-zx">
          <SectionHeader
            overline="Testimonials"
            title="What partners say about working with us"
            testid="testimonials-section-header"
          />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={t.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-surface p-6"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-5 w-5 text-cyan" />
                <blockquote className="mt-3 text-[15px] leading-relaxed text-white/90">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-5 border-t border-white/[0.06] pt-4 flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-full grid place-items-center font-display text-sm font-bold text-[#071A2D] bg-gradient-to-br ${t.accent || "from-cyan to-royal"} shadow-glow`}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{t.name}</div>
                    <div className="text-xs text-ink-400">{t.role}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="container-zx">
          <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] p-10 md:p-16" style={{ background: "linear-gradient(120deg, #0B2138 0%, #0F2B48 50%, #143861 100%)" }}>
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-cyan/15 blur-3xl" />
            <div className="relative grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <div className="overline mb-2">Ready when you are</div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
                  Let&apos;s engineer the next system your business depends on.
                </h3>
                <p className="mt-3 text-ink-200 max-w-2xl">
                  A 30-minute consultation, a working blueprint, and clarity on what to build next.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <LeadDialog
                  source="home_bottom_cta"
                  titleVariant="expert"
                  trigger={
                    <button type="button" className="btn-primary" data-testid="cta-section-primary">
                      Talk to an Expert <ArrowRight className="h-4 w-4" />
                    </button>
                  }
                />
                <a
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                  data-testid="cta-section-secondary"
                >
                  <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function WorldMap() {
  // Stylised dotted globe — Mumbai + Dubai highlighted.
  const dots = [];
  const cols = 80;
  const rows = 32;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const nx = x / cols;
      const ny = y / rows;
      // Rough world silhouette via sin functions to avoid heavy assets
      const v = Math.sin(nx * Math.PI * 2) * 0.18 + Math.cos((ny + 0.1) * Math.PI) * 0.32;
      const inLand = Math.abs(ny - 0.5 + v) < 0.16 && !(nx > 0.05 && nx < 0.12 && ny > 0.6);
      if (inLand) dots.push([nx, ny]);
    }
  }
  // Approximate map coords for Mumbai & Dubai on the stylised canvas
  const pins = [
    { x: 0.65, y: 0.52, label: "Mumbai" },
    { x: 0.595, y: 0.48, label: "Dubai" },
  ];
  return (
    <svg viewBox="0 0 800 500" className="absolute inset-0 h-full w-full">
      <defs>
        <radialGradient id="bg" cx="50%" cy="50%" r="65%">
          <stop offset="0%" stopColor="#16D5FF" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#071A2D" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="500" fill="url(#bg)" />
      {dots.map(([nx, ny], i) => (
        <circle key={i} cx={nx * 800} cy={ny * 500} r={1.2} fill="rgba(142,166,193,0.35)" />
      ))}
      {pins.map((p) => (
        <g key={p.label}>
          <circle cx={p.x * 800} cy={p.y * 500} r={22} fill="rgba(22,213,255,0.10)">
            <animate attributeName="r" values="14;26;14" dur="2.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx={p.x * 800} cy={p.y * 500} r={5} fill="#16D5FF" />
          <text x={p.x * 800 + 12} y={p.y * 500 + 4} fill="#FFFFFF" fontFamily="Inter" fontSize="12" fontWeight="600">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}
